import {
  Alert,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import classNames from "classnames";
import React, {
  useState,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import PropTypes from "prop-types";
import PageHeader from "../common/PageHeader";
import withRedirect from "../../helpers/hoc/withRedirect";
import {
  faBuilding,
  faCheckCircle,
  faExclamationTriangle,
  faInfoCircle,
  faMoneyBillWave,
  faMoneyCheck,
  faSearch,
  faSpinner,
  faSyncAlt,
  faTimes,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import {
  executeDebtsToPayJobs,
  fetchPlanillaSteps,
  executeJobs,
  fetchAliquots,
  fetchSingleFormReports,
  sendEmail,
  sendEmailAdmin,
} from "../../helpers/api/finance";
import PlanillaSteps from "./PlanillaSteps";
import PeriodSelect from "../common/PeriodSelect";
import Datetime from "react-datetime";
import { useSelector } from "react-redux";
import SupportLink from "../root/support/components/SupportLink";
import NumberFormat from "react-number-format";
import moment from "moment";
import Logo from "../navbar/Logo";
import useSWRFetch from "../../helpers/hooks/useSWRFetch";
import useSWRFetchPagination from "../../helpers/hooks/useSWRFetchPagination";
import FullPagination from "../common/pagination/FullPagination";
import { findOneConfig } from "../../helpers/api/condominium";
import { CurrencyContext } from "../../context/Context";
import ItemImageTitleSubProperty from "../common/ItemImageTitleSubProperty";
import SelectPropertyListItem from "../common/SelectPropertyListItem";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ButtonIcon from "../common/ButtonIcon";
import {
  Link,
  useHistory,
  useParams,
} from "react-router-dom/cjs/react-router-dom.min";
import {
  ERROR_RESPONSE,
  SUCCESS_RESPONSE,
} from "../../helpers/codeResponses/codes";
import { Toast } from "../UI/toast/Toast";
import Loader from "../common/Loader";
import { checkUserToken } from "../../helpers/api/auth";
import OnboardingBanner from "../onboarding/OnboardingBanner";
import useOnboardingStepParam from "../../helpers/hooks/useOnboardingStepParam";
import { setOnboardingStepDismissed } from "../../helpers/api/onboarding";
import useModuleBase from "../../helpers/hooks/useModuleBase";

const SingleForm = () => {
  const moduleBase = useModuleBase();
  const reportRef = useRef();
  const { selectedCondominium } = useSelector((state) => state.condominium);
  const { data: dataConfig, isLoading: loadingConfig } = useSWRFetch(
    selectedCondominium,
    findOneConfig
  );

  const { id } = useParams();
  const { condominium, address, phone } = useSelector(
    (state) => state.condominium
  );

  // HO-HU-36: "previsualizar la planilla" es el unico paso de la guia que no
  // deja rastro en los datos, asi que se cierra al haberla visto. Se llama a
  // la API directo en vez de montar useOnboardingProgress: esta pantalla ya
  // es pesada y no necesita suscribirse al estado completo de la guia (al
  // volver al Inicio, el checklist revalida solo). El ref evita repetir el
  // POST si el componente se re-renderiza.
  const onboardingStep = useOnboardingStepParam();
  const previewStepMarked = useRef(false);

  useEffect(() => {
    if (
      onboardingStep !== "preview" ||
      !selectedCondominium ||
      previewStepMarked.current
    ) {
      return;
    }

    previewStepMarked.current = true;
    setOnboardingStepDismissed(selectedCondominium, "preview").catch(() => {
      // Si no se pudo marcar, el paso sigue pendiente en la guia: es
      // preferible a interrumpir la previsualizacion con un error.
      previewStepMarked.current = false;
    });
  }, [onboardingStep, selectedCondominium]);
  const { token } = useSelector((state) => state.auth.user);
  const history = useHistory();
  const { currency, baseCurrency } = useContext(CurrencyContext);

  // Fecha de la tasa con la que se convierten los montos a la moneda
  // secundaria, igual que en la planilla de Reportes (HO-TASK-240). Arranca en
  // hoy, que es lo que hacía esta vista antes. rateDateInput es lo que se ve en
  // el filtro; appliedRateDate es lo que se aplicó al pulsar "Buscar".
  const today = moment().format("YYYY-MM-DD");
  const [rateDateInput, setRateDateInput] = useState(today);
  const [appliedRateDate, setAppliedRateDate] = useState(today);

  const [reportTotals, setReportTotals] = useState(null);
  const previewRate = reportTotals?.exchange_rate || null;
  const rateValue = Number(previewRate?.value || currency?.value || 0);
  const rateDateKey = previewRate?.date || null;
  const isExactDate = previewRate ? Boolean(previewRate.is_exact) : true;
  const isRateLoading = false;

  // HO-TASK-261 / HO-TASK-262: la tasa de cada línea la resuelve el backend
  // con la fecha del gasto (`exchange_rate`, misma regla que el reporte de la
  // planilla emitida). Antes se buscaba acá contra el catálogo de tasas y, si
  // faltaba, se pedía la de ese día a un servicio externo por cada fila.
  const getRateForRow = (row) => Number(row?.exchange_rate?.value) || 0;

  // Los gastos traen el monto en `value` y los fondos e ingresos en `amount`.
  const rowAmount = (row) => Number(row?.amount ?? row?.value ?? 0);

  // Total en bolívares de una sección: cada fila a la tasa de su fecha. Por
  // propietario se mantiene la tasa del día elegido, que es lo que paga hoy.
  const getConvertedTotal = (rows, baseSum, key) => {
    if (rows.length <= 0) return 0;
    const fallback = isPropertyView
      ? baseSum * rateValue
      : rows.reduce(
          (prev, next) =>
            prev +
            (next?.amount_converted !== undefined && next?.amount_converted !== null
              ? Number(next.amount_converted)
              : getRateForRow(next) * rowAmount(next)),
          0
        );
    return key ? sectionBs(key, fallback) : fallback;
  };

  const appliedRateDateLabel = moment(appliedRateDate).format("DD MMM YYYY");
  const rateDateLabel = rateDateKey
    ? moment(rateDateKey).format("DD MMM YYYY")
    : null;
  // Solo se avisa cuando la tasa aplicada no es la de la fecha pedida: si no,
  // la previsualización mostraría bolívares de otro día sin decirlo.
  const rateFallbackNotice = isExactDate
    ? null
    : rateDateLabel
      ? `No hay tasa registrada para el ${appliedRateDateLabel}. Se aplica la del ${rateDateLabel}, que era la vigente ese día.`
      : `No hay tasa registrada para el ${appliedRateDateLabel}. Se aplica la tasa activa.`;

  const [periodId, setPeriodId] = useState();
  // Guarda el objeto período completo (no solo el id) del último select usado entre los 4
  // modales que comparten periodId — lo necesita el preview de descuento por pronto pago del
  // primer modal (discount_percentage/discount_due_date), igual que antes con
  // periods.find(p => p.id === periodId) sobre la lista completa.
  const [selectedPeriodObject, setSelectedPeriodObject] = useState(null);
  const handlePeriodSelectChange = (newId, period) => {
    setPeriodId(newId ? Number(newId) : undefined);
    setSelectedPeriodObject(period || null);
  };
  const [modalIsOpenPeriodEmail, setModalIsOpenPeriodEmail] = useState(false);
  const [isLoadingAdmin, setIsLoadingAdmin] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [modalIsOpenAdmin, setModalIsOpenAdmin] = useState(false);
  const [userData, setUserData] = useState({});
  const [modalIsOpenPeriod, setModalIsOpenPeriod] = useState(false);
  const [modalIsOpenPeriodRegister, setModalIsOpenPeriodRegister] =
    useState(false);
  const [rawReport, setRawReport] = useState([]);
  const [rawReportE, setRawReportE] = useState([]);
  const [rawReportI, setRawReportI] = useState([]);
  const [rawFunds, setRawFunds] = useState([]);
  const [rawIncomes, setRawIncomes] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExecutedForPeriod, setIsExecutedForPeriod] = useState(false);
  const [isEmitting, setIsEmitting] = useState(false);
  const [planillaStatus, setPlanillaStatus] = useState(null);

  const loadPlanillaStatus = useCallback(() => {
    if (!id) return;
    fetchPlanillaSteps(id)
      .then((response) => setPlanillaStatus(response?.data ?? null))
      .catch(() => setPlanillaStatus(null));
  }, [id]);

  useEffect(() => {
    loadPlanillaStatus();
  }, [loadPlanillaStatus]);

  const [autoPayEnabled, setAutoPayEnabled] = useState(false);
  const [emissionNote, setEmissionNote] = useState("");

  useEffect(() => {
    if (!id) return;
    findOneConfig(id)
      .then((response) => setAutoPayEnabled(Boolean(response?.data?.auto_pay_with_credit_balance)))
      .catch(() => setAutoPayEnabled(false));
  }, [id]);

  const markPlanillaStep = (period, step) =>
    setPlanillaStatus((previous) => ({
      period,
      registered: true,
      emitted:
        step === "emitted" ||
        (previous?.period?.id === period?.id && Boolean(previous?.emitted)),
    }));

  // Ocupado = trayendo la previsualización o resolviendo la tasa. La tasa puede
  // tardar porque, si no hay una registrada para la fecha, se consulta la
  // oficial de ese día contra un servicio externo.
  const isBusy = isLoading || isRateLoading;

  // Selector de propietario: permite ver el total del condominio (por defecto)
  // o como quedarían las cuentas para un propietario específico.
  const [viewMode, setViewMode] = useState("condominium");
  const [selectedAliquot, setSelectedAliquot] = useState(null);
  const selectedPropertyId = selectedAliquot?.property?.id ?? null;
  const [propertySearchInput, setPropertySearchInput] = useState("");
  const [aliquotsParams, setAliquotsParams] = useState({
    condominiumId: selectedCondominium,
    state: 0,
    search: "",
  });

  const {
    data: aliquotsList,
    isLoading: isLoadingAliquots,
    meta: aliquotsMeta,
    handler: {
      nextPage: aliquotsNextPage,
      previousPage: aliquotsPreviousPage,
      goToPage: aliquotsGoToPage,
      perPage: aliquotsPerPage,
    },
  } = useSWRFetchPagination(fetchAliquots, aliquotsParams, null, 1, 8);

  const handleSearchProperty = (value = propertySearchInput) => {
    const term = typeof value === "string" ? value.trim() : "";
    setPropertySearchInput(term);
    setAliquotsParams((prev) => ({ ...prev, search: term }));
    aliquotsGoToPage(1);
  };

  useEffect(() => {
    setAliquotsParams((prev) => ({ ...prev, condominiumId: selectedCondominium }));
  }, [selectedCondominium]);

  const fetchApireports = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetchSingleFormReports(selectedCondominium, {
        property_id: selectedPropertyId,
        exchange_rate_date: appliedRateDate,
      });
      setReportTotals({
        totals: response?.totals || null,
        property_totals: response?.property_totals || null,
        exchange_rate: response?.exchange_rate || null,
      });
      const financeType1Reports =
        response?.finances &&
        response?.finances.filter((report) => report?.type?.id === 1);
      const financeType2Reports =
        response?.finances &&
        response?.finances.filter((report) => report?.type?.id === 2);
      const financeType3Reports =
        response?.finances &&
        response?.finances.filter((report) => report?.type?.id === 3);

      setRawReport(financeType1Reports);
      setRawReportE(financeType2Reports);
      setRawReportI(financeType3Reports);
    } catch (error) {
      setReportTotals(null);
    } finally {
      setIsLoading(false);
    }
  }, [selectedCondominium, selectedPropertyId, appliedRateDate]);

  useEffect(() => {
    fetchApireports();
  }, [fetchApireports]);

  const isPropertyView = viewMode === "property" && Boolean(selectedAliquot);
  const aliquotFactor = isPropertyView
    ? Number(selectedAliquot.value) / 100
    : 1;
  const sectionBs = (key, fallback) => {
    const source = isPropertyView ? reportTotals?.property_totals : reportTotals?.totals;
    const value = source?.[`${key}_bs`];
    return value !== undefined && value !== null ? Number(value) : fallback;
  };
  const backendTotal = (key) => {
    const source = isPropertyView ? reportTotals?.property_totals : reportTotals?.totals;
    const value = source?.[key];
    return value !== undefined && value !== null ? Number(value) : null;
  };

  const report = useMemo(() => {
    if (!isPropertyView) return rawReport;
    return rawReport.map((c) => ({
      ...c,
      total_amount: c.value,
      total_amount_converted: c.amount_converted,
      value: c.property_share ?? c.value * aliquotFactor,
    }));
  }, [rawReport, isPropertyView, aliquotFactor]);

  useEffect(()=>{
    console.log("report: ", report)
  },[report]);

  const reportE = useMemo(() => {
    if (!isPropertyView) return rawReportE;
    return rawReportE.map((c) => ({
      ...c,
      total_amount: c.value,
      total_amount_converted: c.amount_converted,
      value: c.property_share ?? c.value * aliquotFactor,
    }));
  }, [rawReportE, isPropertyView, aliquotFactor]);

  const reportI = useMemo(() => {
    if (!isPropertyView) return rawReportI;
    return rawReportI
      .map((c) => {
        const entry = c?.properties?.find(
          (p) => p?.property?.id === selectedAliquot?.property?.id
        );
        if (!entry) return null;
        return {
          ...c,
          total_amount: c.value,
          total_amount_converted: c.amount_converted,
          value: Number(c.property_share ?? entry.amount),
        };
      })
      .filter(Boolean);
  }, [rawReportI, isPropertyView, selectedAliquot]);

  const funds = useMemo(() => {
    if (!isPropertyView) return rawFunds;
    return rawFunds.map((c) => ({
      ...c,
      total_amount: c.amount,
      total_amount_converted: c.amount_converted,
      amount: Number(c.property_share ?? Number(c.amount) * aliquotFactor),
    }));
  }, [rawFunds, isPropertyView, aliquotFactor]);

  const incomesCommon = useMemo(() => {
    const common = rawIncomes.filter((income) => income.is_common_type);
    if (!isPropertyView) return common;
    return common.map((income) => ({
      ...income,
      total_amount: income.amount,
      total_amount_converted: income.amount_converted,
      amount: Number(
        income.property_share ?? Number(income.amount) * aliquotFactor
      ),
    }));
  }, [rawIncomes, isPropertyView, aliquotFactor]);

  const incomesIndividual = useMemo(() => {
    const individual = rawIncomes.filter((income) => !income.is_common_type);
    if (!isPropertyView) return individual;
    return individual
      .filter((income) => income?.property_id === selectedAliquot?.property?.id)
      .map((income) => ({
        ...income,
        total_amount: income.amount,
        total_amount_converted: income.amount_converted,
      }));
  }, [rawIncomes, isPropertyView, selectedAliquot]);

  const amount = useMemo(
    () => Number(report.reduce((a, c) => a + Number(c?.value || 0), 0)).toFixed(2),
    [report]
  );
  const amountE = useMemo(
    () => Number(reportE.reduce((a, c) => a + Number(c?.value || 0), 0)).toFixed(2),
    [reportE]
  );
  const amountI = useMemo(
    () => Number(reportI.reduce((a, c) => a + Number(c?.value || 0), 0)).toFixed(2),
    [reportI]
  );
  const amountF = useMemo(
    () => Number(funds.reduce((a, c) => a + Number(c?.amount || 0), 0)).toFixed(2),
    [funds]
  );
  const amountIncomesCommon = useMemo(
    () => Number(incomesCommon.reduce((a, c) => a + Number(c?.amount || 0), 0)).toFixed(2),
    [incomesCommon]
  );
  const amountIncomesIndividual = useMemo(
    () => Number(incomesIndividual.reduce((a, c) => a + Number(c?.amount || 0), 0)).toFixed(2),
    [incomesIndividual]
  );
  const rawCondominiumTotal = useMemo(() => {
    const rawF = (rawFunds || []).reduce((a, c) => a + Number(c?.amount || 0), 0);
    const rawOrd = (rawReport || []).reduce((a, c) => a + Number(c?.value || 0), 0);
    const rawExt = (rawReportE || []).reduce((a, c) => a + Number(c?.value || 0), 0);
    const rawInd = (rawReportI || []).reduce((a, c) => a + Number(c?.value || 0), 0);
    const rawIncCom = (rawIncomes || [])
      .filter((i) => Boolean(i?.is_common_type))
      .reduce((a, c) => a + Number(c?.amount || 0), 0);
    const rawIncInd = (rawIncomes || [])
      .filter((i) => Boolean(i && !i.is_common_type))
      .reduce((a, c) => a + Number(c?.amount || 0), 0);
    const fromBackend = reportTotals?.totals?.total;
    if (fromBackend !== undefined && fromBackend !== null) return Number(fromBackend);
    return rawF + rawOrd + rawExt + rawInd - rawIncCom - rawIncInd;
  }, [rawFunds, rawReport, rawReportE, rawReportI, rawIncomes, reportTotals]);

  const startsNewPlanilla =
    !isExecutedForPeriod && Boolean(planillaStatus?.emitted) && rawCondominiumTotal > 0;
  const currentPlanilla = startsNewPlanilla ? null : planillaStatus;
  const planillaPeriod = currentPlanilla?.period ?? null;
  const isRegistered = Boolean(currentPlanilla?.registered || currentPlanilla?.emitted);
  const isEmitted = Boolean(currentPlanilla?.emitted);
  const planillaStep = isEmitted ? 3 : isRegistered ? 2 : 1;

  const total = useMemo(() => {
    const fromBackend = backendTotal("total");
    if (fromBackend !== null) return fromBackend;
    return Math.max(
      [amountF, amount, amountE, amountI].reduce(
        (a, c) => Number(a) + Number(c),
        0
      ) -
        Number(amountIncomesCommon) -
        Number(amountIncomesIndividual),
      0
    );
  }, [amountF, amount, amountE, amountI, amountIncomesCommon, amountIncomesIndividual, isPropertyView, reportTotals]);

  // HO-TASK-261: "Total gastos condominio" en bolívares es la suma de cada
  // gasto a la tasa de su fecha, menos los ingresos a la suya. El total por
  // propietario sigue a la tasa del día elegido: es lo que paga hoy.
  const convertedTotal = useMemo(() => {
    const fromBackend = sectionBs("total", null);
    if (fromBackend !== null) return fromBackend;
    if (isPropertyView) return total * (rateValue || 0);
    const expenses =
      getConvertedTotal(report, amount, "ordinary") +
      getConvertedTotal(reportE, amountE, "extraordinary") +
      getConvertedTotal(reportI, amountI, "particular") +
      // Los fondos de reserva no nacen de un gasto: van con la tasa del día
      // elegido arriba, la misma del total a pagar.
      sectionBs("funds", Number(amountF) * (rateValue || 0));
    const incomes =
      getConvertedTotal(incomesCommon, amountIncomesCommon, "incomes_common") +
      getConvertedTotal(incomesIndividual, amountIncomesIndividual, "incomes_individual");
    return Math.max(expenses - incomes, 0);
  }, [isPropertyView, total, rateValue, report, reportE, reportI, funds, incomesCommon, incomesIndividual]);

  const loadData = () => {
    setUserData({});
    checkUserToken(token)
      // Se guarda el recurso completo, no su campo `data`. `data` es la persona
      // (PeopleResource) y su id es el de la tabla people, no el de la cuenta:
      // el envío al administrador se lo pasaba así al backend, que busca un
      // AuthCredential con ese número y terminaba mandando el correo a la
      // cuenta equivocada. Mismo arreglo que en Reports.jsx.
      .then((auth) => {
        setUserData(auth || {});
      })
      .catch((error) => setError(error));
  };

  useEffect(() => {
    loadData();
    return () => setUserData({});
    // eslint-disable-next-line
  }, []);

  // Los 4 modales de período de abajo tienen cada uno su propio PeriodSelect (con su propia
  // paginación); reloadAllPeriodSelects() refresca los 4 a la vez, para los mismos casos en que
  // antes se refrescaba la lista compartida `periods` (ej. al volver a ejecutar la planilla).
  const periodSelectRefs = useRef({});
  const reloadAllPeriodSelects = () => {
    Object.values(periodSelectRefs.current).forEach((ref) => ref?.reload());
  };

  const fetchApireportsFunds = useCallback(async () => {
    try {
      const response = await fetchSingleFormReports(selectedCondominium, {
        property_id: selectedPropertyId,
        exchange_rate_date: appliedRateDate,
      });
      setRawFunds(
        response.reserveFundsPayments ? response.reserveFundsPayments : []
      );
      setRawIncomes(response.incomes ? response.incomes : []);
    } catch (error) {
      setRawFunds([]);
      setRawIncomes([]);
    }
  }, [selectedCondominium, selectedPropertyId, appliedRateDate]);

  useEffect(() => {
    fetchApireportsFunds();
  }, [fetchApireportsFunds]);

  const openModalPeriod = () => {
    if (rawCondominiumTotal <= 0) {
      Toast.fire({
        type: "warning",
        title: "El monto total de la planilla debe ser mayor a 0 para poder emitir o registrar el cobro.",
      });
      return;
    }
    const pendingEmission = isRegistered && !isEmitted ? planillaPeriod : null;
    setPeriodId(pendingEmission?.id);
    setSelectedPeriodObject(pendingEmission);
    setEmissionNote(dataConfig?.data?.note ?? "");
    setModalIsOpenPeriod(true);
  };

  const closeModalPeriodEMail = () => {
    setModalIsOpenPeriodEmail(false);
  };

  const closeModal = () => {
    setModalIsOpen(false);
  };

  const closeModalAdmin = () => {
    setModalIsOpenAdmin(false);
  };
  const closeModalPeriod = () => {
    setModalIsOpenPeriod(false);
  };
  const openModalPeriodRegister = () => {
    if (rawCondominiumTotal <= 0) {
      Toast.fire({
        type: "warning",
        title: "El monto total de la planilla debe ser mayor a 0 para poder emitir o registrar el cobro.",
      });
      return;
    }
    setPeriodId(undefined);
    setSelectedPeriodObject(null);
    setModalIsOpenPeriodRegister(true);
  };

  const closeModalPeriodRegister = () => {
    setModalIsOpenPeriodRegister(false);
  };

  const handleExecuteJobs = async (period) => {
    if (isEmitting) return;
    setIsEmitting(true);
    try {
      const response = await executeJobs(id, period, autoPayEnabled, emissionNote);

      if (response.error) {
        return;
      }

      const { code } = response;

      if (code === SUCCESS_RESPONSE.OK) {
        setIsExecutedForPeriod(true);
        markPlanillaStep(selectedPeriodObject ?? { id: period }, "emitted");
        Toast.fire({
          type: "success",
          title: autoPayEnabled
            ? `Efectos por cobrar actualizados exitosamente. En unos segundos se pagan con su saldo a favor las planillas de quienes lo tengan.`
            : `Efectos por cobrar actualizados exitosamente.`,
        });
        closeModalPeriod();
      } else if (code === ERROR_RESPONSE.PERIOD_EXIST) {
        setIsExecutedForPeriod(true);
        markPlanillaStep(selectedPeriodObject ?? { id: period }, "emitted");
        Toast.fire({
          type: "warning",
          title: `Ya existen cobros con el período seleccionado.`,
        });
        closeModalPeriod();
      } else if (code === ERROR_RESPONSE.PERIOD_NO_EXIST) {
        Toast.fire({
          type: "warning",
          title: `Primero registra los gastos de este período (paso 1).`,
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsEmitting(false);
    }
  };

  const handleSendEmail = async (period) => {
    try {
      const response = await sendEmail(id, period, {
        exchangeRate: rateValue,
        exchangeRateDate: appliedRateDate,
      });
      const responseData = response?.data ?? response;
      const code = responseData?.code ?? response?.code;
      const isSuccess =
        code === SUCCESS_RESPONSE.OK ||
        code === SUCCESS_RESPONSE.SUCCESFUL ||
        code === SUCCESS_RESPONSE.CREATED ||
        code === SUCCESS_RESPONSE.ADDED_TO_QUEUE;

      if (isSuccess) {
        history.push({
          pathname: `/dashboard/${moduleBase}/${id}/form`,
        });

        Toast.fire({
          type: "success",
          title: `Avisos enviados exitosamente.`,
        });
      } else if (code) {
        Toast.fire({
          type: "warning",
          title: `No se pudo enviar el aviso.`,
        });
      }
    } catch (error) {
      console.error(error);
      Toast.fire({
        type: "error",
        title: `No se pudo enviar el aviso.`,
      });
    }
    closeModal();
  };

  const handleExecuteDebtsToPayJobs = async (period) => {
    try {
      const response = await executeDebtsToPayJobs(id, period);

      if (response.error) {
        return;
      }

      const { code } = response;

      if (code === SUCCESS_RESPONSE.OK) {
        markPlanillaStep(selectedPeriodObject ?? { id: period }, "registered");
        Toast.fire({
          type: "success",
          title: `Cuentas por pagar actualizadas exitosamente. Ahora emite la planilla (paso 2).`,
        });
        closeModalPeriodRegister();
      } else if (code === ERROR_RESPONSE.PERIOD_EXIST) {
        markPlanillaStep(selectedPeriodObject ?? { id: period }, "registered");
        Toast.fire({
          type: "info",
          title: `Los gastos de este período ya estaban registrados. Ahora emite la planilla (paso 2).`,
        });
        closeModalPeriodRegister();
      } else if (code === ERROR_RESPONSE.NO_FINANCES_TO_PROCESS) {
        Toast.fire({
          type: "warning",
          title: `No hay gastos ni fondos de reserva por registrar en este período.`,
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleSendEmailAdmin = async (period) => {
    setIsLoadingAdmin(true);
    try {
      const response = await sendEmailAdmin(id, period, userData.id, {
        exchangeRate: rateValue,
        exchangeRateDate: appliedRateDate,
      });
      const responseData = response?.data ?? response;
      const code = responseData?.code ?? response?.code;
      const isSuccess =
        code === SUCCESS_RESPONSE.OK ||
        code === SUCCESS_RESPONSE.SUCCESFUL ||
        code === SUCCESS_RESPONSE.CREATED ||
        code === SUCCESS_RESPONSE.ADDED_TO_QUEUE;

      if (isSuccess) {
        history.push({
          pathname: `/dashboard/${moduleBase}/${id}/form`,
        });

        Toast.fire({
          type: "success",
          title: `Avisos enviados exitosamente.`,
        });
      } else if (code) {
        Toast.fire({
          type: "warning",
          title: `No se pudo enviar el aviso.`,
        });
      }
    } catch (error) {
      console.error(error);
      Toast.fire({
        type: "error",
        title: `No se pudo enviar el aviso.`,
      });
    } finally {
      setIsLoadingAdmin(false);
    }
    closeModalAdmin();
  };
  const estimateTotalCell = (row, rate) => {
    const total = row?.total_amount;
    const totalBs =
      row?.total_amount_converted ??
      (total !== undefined && total !== null ? Number(total) * (rate || 0) : null);

    return (
      <td className="align-middle text-right" style={{ width: "15%" }}>
        {total === undefined || total === null ? (
          "-"
        ) : (
          <>
            <span className="d-block font-weight-semi-bold">
              <NumberFormat
                className="text-nowrap"
                displayType="text"
                prefix={`${baseCurrency.symbol} `}
                allowedDecimalSeparators={true}
                decimalScale={2}
                fixedDecimalScale
                thousandSeparator={true}
                value={total}
              />
            </span>
            <NumberFormat
              className="text-nowrap fs--2 text-600"
              displayType="text"
              prefix={`${currency?.currency?.iso || "Bs"} `}
              allowedDecimalSeparators={true}
              decimalScale={2}
              fixedDecimalScale
              thousandSeparator={true}
              value={totalBs ?? 0}
            />
          </>
        )}
      </td>
    );
  };

  return (
    <>
      <PageHeader
        title="Previsualizacion de planilla única de gastos"
        titleTag="h2"
        description="Gestiona y administra cada una de tus finanzas."
        className="mb-3"
        col={{ lg: 9 }}
      >
        <SupportLink />
      </PageHeader>
      <OnboardingBanner steps={["preview"]} />
      <Card className="mb-3">
        <CardBody className="py-3">
          {rawCondominiumTotal <= 0 && (
            <Alert color="warning" className="mb-3 shadow-sm">
              <div className="d-flex align-items-center">
                <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2 fs-1" />
                <div>
                  <strong>Atención:</strong> El monto neto total de esta planilla es{" "}
                  <strong>
                    {baseCurrency.symbol} {rawCondominiumTotal.toFixed(2)}
                  </strong>
                  . No se permite registrar ni emitir planillas de cobro cuando el total es 0 o negativo.
                </div>
              </div>
            </Alert>
          )}
          <PlanillaSteps
            current={planillaStep}
            steps={[
              {
                key: "register",
                title: "Registrar gastos",
                description:
                  "Pasa los gastos del período a las cuentas por pagar del condominio.",
                done: isRegistered,
                doneDetail: planillaPeriod?.name,
                renderAction: (state) => (
                  <ButtonIcon
                    color={state === "current" ? "primary" : "falcon-default"}
                    size="sm"
                    onClick={openModalPeriodRegister}
                    icon={faMoneyBillWave}
                    disabled={rawCondominiumTotal <= 0}
                  >
                    Registrar
                  </ButtonIcon>
                ),
              },
              {
                key: "emit",
                title: "Emitir planilla",
                description:
                  "Le cobra a cada propietario su parte, según su alícuota.",
                note: autoPayEnabled
                  ? "Pago con saldo a favor activado: al emitir, se paga con el saldo de quien lo tenga."
                  : null,
                done: isEmitted,
                doneDetail: planillaPeriod?.name,
                renderAction: (state) => (
                  <ButtonIcon
                    color={state === "current" ? "primary" : "falcon-default"}
                    size="sm"
                    onClick={openModalPeriod}
                    icon={faMoneyCheck}
                    disabled={rawCondominiumTotal <= 0}
                  >
                    Emitir
                  </ButtonIcon>
                ),
              },
              {
                key: "send",
                title: "Enviar a propietarios",
                description:
                  "Desde Reportes, le envía a cada propietario su planilla por correo.",
                renderAction: (state) => (
                  <Link
                    className={classNames(
                      "btn btn-sm",
                      state === "current" ? "btn-primary" : "btn-falcon-default"
                    )}
                    to={`/dashboard/${moduleBase}/${id}/reports`}
                  >
                    Ir a Reportes
                  </Link>
                ),
              },
            ]}
          />
        </CardBody>
      </Card>
      <Card className="mb-3">
        <CardBody className="py-3">
          <Row form className="align-items-end">
            <Col md={4} sm={12} tag={FormGroup} className="mb-2 mb-md-0">
              <Label>Fecha de la tasa de cambio</Label>
              <Datetime
                value={
                  rateDateInput ? moment(rateDateInput, "YYYY-MM-DD") : ""
                }
                onChange={(picked) => {
                  if (moment.isMoment(picked) && picked.isValid()) {
                    setRateDateInput(picked.format("YYYY-MM-DD"));
                    return;
                  }
                  const parsed = moment(
                    picked,
                    ["DD MMM YYYY", "YYYY-MM-DD"],
                    true
                  );
                  setRateDateInput(
                    parsed.isValid() ? parsed.format("YYYY-MM-DD") : ""
                  );
                }}
                dateFormat="DD MMM YYYY"
                timeFormat={false}
                closeOnSelect
                isValidDate={(current) => !current.isAfter(moment(), "day")}
                inputProps={{ placeholder: "DD MMM YYYY" }}
              />
            </Col>
            <Col md={3} sm={12} className="mb-2 mb-md-0">
              <ButtonIcon
                onClick={() => setAppliedRateDate(rateDateInput || today)}
                className="text-nowrap"
                disabled={isBusy}
                icon={isBusy ? faSpinner : faSearch}
                iconClassName={isBusy ? "fa-spin" : ""}
                color="falcon-default"
              >
                {isBusy ? "Buscando..." : "Buscar"}
              </ButtonIcon>
            </Col>
            <Col md={5} sm={12} className="mb-2 mb-md-0">
              {isRateLoading ? (
                <p className="fs--1 mb-0 text-600">Consultando tasa...</p>
              ) : (
                currency?.currency?.iso &&
                rateValue > 0 && (
                  <>
                    <p className="fs--1 mb-0 text-600">
                      Tasa aplicada:{" "}
                      <strong>
                        {baseCurrency.symbol} 1,00 = {currency.currency.iso}{" "}
                        <NumberFormat
                          displayType="text"
                          decimalScale={2}
                          fixedDecimalScale
                          thousandSeparator
                          value={rateValue}
                        />
                      </strong>
                      {rateDateLabel ? ` (${rateDateLabel})` : ""}
                    </p>
                    {rateFallbackNotice && (
                      <p className="fs--1 mb-0 text-warning">
                        {rateFallbackNotice}
                      </p>
                    )}
                  </>
                )
              )}
            </Col>
          </Row>
        </CardBody>
      </Card>
      <Card
        className="mb-3"
        innerRef={(e) => {
          reportRef.current = e;
        }}
      >
        {isExecutedForPeriod ? (
          <CardBody className="text-center p-5">
            <FontAwesomeIcon
              icon={faCheckCircle}
              className="text-success mb-3"
              style={{ fontSize: "3.5rem" }}
            />
            <h4 className="mb-2">Planilla generada para este período</h4>
            <p className="text-muted fs-0 mb-4">
              Se ha realizado el cobro de gastos exitosamente. El período ha sido generado e ingresado a las cuentas por cobrar.
            </p>
            <p className="fs-0 mb-4">
              Último paso: envía las planillas a los propietarios desde{" "}
              <Link to={`/dashboard/${moduleBase}/${id}/reports`}>Reportes</Link>
              , con «Enviar planillas a propietarios».
            </p>
            <Button
              color="primary"
              onClick={() => {
                setIsExecutedForPeriod(false);
                loadPlanillaStatus();
                fetchApireports();
                fetchApireportsFunds();
                reloadAllPeriodSelects();
              }}
            >
              <FontAwesomeIcon icon={faSyncAlt} className="mr-2" />
              Volver a ejecutar planilla
            </Button>
          </CardBody>
        ) : isBusy ? (
          <Row className="p-4 align-items-center justify-content-center text-center mb-3">
            <Col xs={12} className="d-flex flex-column align-items-center">
              <Spinner
                color="primary"
                style={{ width: "3rem", height: "3rem", borderWidth: "0.3rem" }}
              />
              <h6 className="mt-3 mb-0 text-600">
                {isLoading
                  ? "Cargando la previsualización..."
                  : "Consultando la tasa de cambio..."}
              </h6>
            </Col>
          </Row>
        ) : (
          <CardBody>
            <Nav tabs className="mb-4">
              <NavItem>
                <NavLink
                  className={classNames({ active: viewMode === "condominium" }, "py-2 px-3")}
                  onClick={() => setViewMode("condominium")}
                  style={{ cursor: "pointer" }}
                >
                  <FontAwesomeIcon icon={faBuilding} className="mr-2" />
                  Condominio completo
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classNames({ active: viewMode === "property" }, "py-2 px-3")}
                  onClick={() => setViewMode("property")}
                  style={{ cursor: "pointer" }}
                >
                  <FontAwesomeIcon icon={faUser} className="mr-2" />
                  Por propietario
                </NavLink>
              </NavItem>
            </Nav>

            {viewMode === "property" && (
              <div className="border-bottom pb-3 mb-4">
                {selectedAliquot ? (
                  <Row className="align-items-center mb-3">
                    <Col>
                      <Alert color="info" className="shadow-sm mb-0 d-flex justify-content-between align-items-center">
                        <span>
                          Mostrando cuentas de{" "}
                          <strong>
                            {selectedAliquot?.property?.user?.data?.firstname}{" "}
                            {selectedAliquot?.property?.user?.data?.lastname}
                          </strong>
                          {selectedAliquot?.property?.property
                            ? ` · Inmueble: ${selectedAliquot.property.property}`
                            : ""}{" "}
                          · Alícuota: {Number(selectedAliquot.value).toFixed(4)}%
                        </span>
                        <Button
                          color="link"
                          size="sm"
                          className="p-0 ml-2"
                          onClick={() => setSelectedAliquot(null)}
                        >
                          Cambiar propietario
                        </Button>
                      </Alert>
                    </Col>
                  </Row>
                ) : (
                  <>
                    <Label for="propertySearch">Buscar propietario</Label>
                    <Row form className="mb-3">
                      <Col sm className="position-relative">
                        <Input
                          id="propertySearch"
                          placeholder="Inmueble, nombre, usuario, cédula, teléfono o correo"
                          bsSize="sm"
                          value={propertySearchInput}
                          onChange={(e) => {
                            const value = e.target.value;
                            setPropertySearchInput(value);
                            if (value.trim() === "") handleSearchProperty("");
                          }}
                          onKeyDown={(e) => {
                            if (e.key !== "Enter") return;
                            e.preventDefault();
                            handleSearchProperty(e.currentTarget.value);
                          }}
                        />
                      </Col>
                      <Col xs="auto">
                        <ButtonIcon
                          icon={faSearch}
                          color="falcon-default"
                          size="sm"
                          onClick={() => handleSearchProperty()}
                        >
                          Buscar
                        </ButtonIcon>
                      </Col>
                    </Row>
                    <div style={{ minHeight: "120px" }}>
                      {isLoadingAliquots ? (
                        <Row className="p-4 align-items-center justify-content-center text-center">
                          <Loader padding="py-3" />
                        </Row>
                      ) : (
                        <>
                          {aliquotsList.map((aliquot) => (
                            <SelectPropertyListItem
                              key={aliquot.id}
                              propertyId={aliquot?.property?.id}
                              selected={selectedAliquot?.property?.id}
                              onSelect={() => setSelectedAliquot(aliquot)}
                              dni={aliquot?.property?.user?.data?.dni}
                              username={aliquot?.property?.user?.username}
                              name={`${aliquot?.property?.user?.data?.firstname || ""} ${
                                aliquot?.property?.user?.data?.lastname || ""
                              }`}
                            />
                          ))}
                          {aliquotsList.length === 0 && (
                            <p className="text-muted fs--1 mb-0">
                              No se encontraron propietarios con alícuotas registradas.
                            </p>
                          )}
                        </>
                      )}
                    </div>
                    {aliquotsList.length > 0 && (
                      <FullPagination
                        nextPage={aliquotsNextPage}
                        prevPage={aliquotsPreviousPage}
                        goToPage={aliquotsGoToPage}
                        meta={aliquotsMeta}
                        className="shadow-none border-top-0"
                        posText="propietarios"
                        perPage={aliquotsPerPage}
                      />
                    )}
                  </>
                )}
              </div>
            )}
            <ItemImageTitleSubProperty
              image={dataConfig?.data?.logo}
              title1={"Previsualizacion de planilla única de gastos"}
              titleCondominium={`Condominio: ${condominium}`}
              condominium={[
                {
                  label: "Documento de identificación",
                  value: phone || "No disponible",
                },

                { label: "Dirección", value: address },
              ]}
              titleProperty={isPropertyView ? "Datos del propietario" : undefined}
              property={
                isPropertyView
                  ? [
                      {
                        label: "Propietario",
                        value: `${selectedAliquot?.property?.user?.data?.firstname || ""} ${
                          selectedAliquot?.property?.user?.data?.lastname || ""
                        }`,
                      },
                      {
                        label: "Documento",
                        value:
                          selectedAliquot?.property?.user?.data?.dni ||
                          "No disponible",
                      },
                      {
                        label: "Inmueble",
                        value:
                          selectedAliquot?.property?.property || "No disponible",
                      },
                      {
                        label: "Alícuota",
                        value: `${Number(selectedAliquot?.value).toFixed(4)}%`,
                      },
                    ]
                  : []
              }
              loading={loadingConfig}
            />

            {report.length > 0 && (
              <div className="table-responsive fs--1">
                <Table striped className="border-bottom">
                  <thead>
                    <tr>
                      <th className="border-0">Gastos ordinarios</th>
                      {!isPropertyView && <th className="border-0">Proveedor</th>}
                      <th className="border-0">Descripción</th>
                      <th className="border-0 text-center" style={{ whiteSpace: "nowrap" }}>Fecha del gasto</th>
                      <th className="border-0 text-center" style={{ whiteSpace: "nowrap" }}>Tasa del día</th>
                      {isPropertyView && <th className="border-0 text-right" style={{ whiteSpace: "nowrap" }}>Monto</th>}
                      <th className="border-0 text-right" style={{ whiteSpace: "nowrap" }}>{isPropertyView ? "Cuota" : "Monto"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.map((cuenta, index) => (
                      <tr key={index}>
                        <td
                          className="align-middle col-3"
                          style={{ width: "30%" }}
                        >
                          {" "}
                          <h6 className="mb-0">{cuenta?.name} </h6>
                        </td>
                        {!isPropertyView && (
                          <td className="align-middle" style={{ width: "20%" }}>
                            {" "}
                            {cuenta.provider_name != null
                              ? cuenta.provider_name
                              : cuenta?.provider?.name}
                          </td>
                        )}
                        <td className="align-middle" style={{ width: "30%" }}>
                          {cuenta?.description}
                        </td>

                        <td className="align-middle">
                          {moment(cuenta.expense_date || cuenta.expired_at).format("DD MMM YYYY")}
                        </td>
                        <td className="align-middle text-right text-nowrap">
                          <NumberFormat
                            displayType="text"
                            prefix={`${currency?.currency?.iso} `}
                            allowedDecimalSeparators={true}
                            decimalScale={2}
                            fixedDecimalScale
                            thousandSeparator={true}
                            value={getRateForRow(cuenta)}
                          />
                        </td>

                        {isPropertyView && estimateTotalCell(cuenta, getRateForRow(cuenta))}
                        <td
                          className="align-middle text-right"
                          style={{ width: "20%" }}
                        >
                          <span className="d-block font-weight-semi-bold">
                            <NumberFormat
                              className="text-nowrap"
                              displayType="text"
                              prefix={`${baseCurrency.symbol} `}
                              allowedDecimalSeparators={true}
                              decimalScale={2}
                              fixedDecimalScale
                              thousandSeparator={true}
                              value={cuenta.value}
                            />
                          </span>
                          <NumberFormat
                            className="text-nowrap fs--2 text-600"
                            displayType="text"
                            prefix={`Bs `}
                            allowedDecimalSeparators={true}
                            decimalScale={2}
                            fixedDecimalScale
                            thousandSeparator={true}
                            value={isPropertyView ? Number(cuenta.value) * getRateForRow(cuenta) : cuenta.amount_converted ?? Number(cuenta.value) * getRateForRow(cuenta)}
                          />
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td className="align-middle" colSpan={5}>Total de gastos ordinarios</td>
                      <td className="align-middle text-right font-weight-semi-bold">
                        <span className="d-block">
                          <NumberFormat
                            className="text-nowrap"
                            displayType="text"
                            prefix={`${baseCurrency.symbol} `}
                            allowedDecimalSeparators={true}
                            decimalScale={2}
                            fixedDecimalScale
                            thousandSeparator={true}
                            value={amount}
                          />
                        </span>
                        {Boolean(currency?.currency?.iso) && (
                          <span className="d-block text-700 fs--2 text-nowrap">
                            <NumberFormat
                              className="text-right text-nowrap"
                              displayType="text"
                              allowedDecimalSeparators={true}
                              thousandSeparator={true}
                              decimalScale={2}
                              fixedDecimalScale
                              prefix={currency.currency.iso + " "}
                              value={getConvertedTotal(report, amount, "ordinary")}
                            />
                          </span>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            )}

            {reportE.length > 0 && (
              <div className="table-responsive mt-4 fs--1">
                <Table striped className="border-bottom">
                  <thead>
                    <tr>
                      <th className="border-0">Gastos extraordinarios</th>
                      {!isPropertyView && <th className="border-0">Proveedor</th>}
                      <th className="border-0">Descripción</th>
                      <th className="border-0" style={{ whiteSpace: "nowrap" }}>Fecha del gasto</th>
                      <th className="border-0 text-right" style={{ whiteSpace: "nowrap" }}>Tasa del día</th>
                      {isPropertyView && <th className="border-0 text-right">Monto</th>}
                      <th className="border-0 text-right">{isPropertyView ? "Cuota" : "Monto"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportE.map((cuenta, index) => (
                      <tr key={index}>
                        <td
                          className="align-middle col-3"
                          style={{ width: "30%" }}
                        >
                          {" "}
                          <h6 className="mb-0">{cuenta?.name} </h6>
                        </td>
                        {!isPropertyView && (
                          <td className="align-middle" style={{ width: "20%" }}>
                            {" "}
                            {cuenta.provider_name != null
                              ? cuenta.provider_name
                              : cuenta?.provider?.name}
                          </td>
                        )}
                        <td className="align-middle" style={{ width: "30%" }}>
                          {cuenta?.description}
                        </td>

                        <td className="align-middle">
                          {moment(cuenta.expense_date || cuenta.expired_at).format("DD MMM YYYY")}
                        </td>
                        <td className="align-middle text-right text-nowrap">
                          <NumberFormat
                            displayType="text"
                            prefix={`${currency?.currency?.iso} `}
                            allowedDecimalSeparators={true}
                            decimalScale={2}
                            fixedDecimalScale
                            thousandSeparator={true}
                            value={getRateForRow(cuenta)}
                          />
                        </td>

                        {isPropertyView && estimateTotalCell(cuenta, getRateForRow(cuenta))}
                        <td
                          className="align-middle text-right"
                          style={{ width: "20%" }}
                        >
                          <span className="d-block font-weight-semi-bold">
                            <NumberFormat
                              className="text-nowrap"
                              displayType="text"
                              prefix={`${baseCurrency.symbol} `}
                              allowedDecimalSeparators={true}
                              decimalScale={2}
                              fixedDecimalScale
                              thousandSeparator={true}
                              value={cuenta.value}
                            />
                          </span>
                          <NumberFormat
                              className="text-nowrap fs--2 text-600"
                              displayType="text"
                              prefix={`Bs `}
                              allowedDecimalSeparators={true}
                              decimalScale={2}
                              fixedDecimalScale
                              thousandSeparator={true}
                              value={isPropertyView ? Number(cuenta.value) * getRateForRow(cuenta) : cuenta.amount_converted ?? Number(cuenta.value) * getRateForRow(cuenta)}
                          />
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td className="align-middle" colSpan={5}>Total de gastos extraordinarios</td>
                      <td className="align-middle text-right font-weight-semi-bold">
                        <span className="d-block">
                          <NumberFormat
                            className="text-nowrap"
                            displayType="text"
                            prefix={`${baseCurrency.symbol} `}
                            allowedDecimalSeparators={true}
                            decimalScale={2}
                            fixedDecimalScale
                            thousandSeparator={true}
                            value={amountE}
                          />
                        </span>
                        {Boolean(currency?.currency?.iso) && (
                          <span className="d-block text-700 fs--2 text-nowrap">
                            <NumberFormat
                              className="text-right text-nowrap"
                              displayType="text"
                              allowedDecimalSeparators={true}
                              thousandSeparator={true}
                              decimalScale={2}
                              fixedDecimalScale
                              prefix={currency.currency.iso + " "}
                              value={getConvertedTotal(reportE, amountE, "extraordinary")}
                            />
                          </span>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            )}
            {reportI.length > 0 && (
              <div className="table-responsive mt-4 fs--1">
                <Table striped className="border-bottom">
                  <thead>
                    <tr>
                      <th className="border-0">Gastos particulares</th>
                      {!isPropertyView && <th className="border-0">Proveedor</th>}
                      <th className="border-0">Descripción</th>
                      <th className="border-0" style={{ whiteSpace: "nowrap" }}>Fecha del gasto</th>
                      <th className="border-0 text-right" style={{ whiteSpace: "nowrap" }}>Tasa del día</th>
                      {isPropertyView && <th className="border-0 text-right">Monto</th>}
                      <th className="border-0 text-right">{isPropertyView ? "Cuota" : "Monto"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <>
                      {reportI.map((cuenta, index) => (
                        <tr key={index}>
                          <td
                            className="align-middle col-3"
                            style={{ width: "30%" }}
                          >
                            {" "}
                            <h6 className="mb-0">{cuenta?.name} </h6>
                          </td>
                          {!isPropertyView && (
                            <td className="align-middle" style={{ width: "20%" }}>
                              {" "}
                              {cuenta.provider_name != null
                                ? cuenta.provider_name
                                : cuenta?.provider?.name}
                            </td>
                          )}
                          <td className="align-middle" style={{ width: "30%" }}>
                            {cuenta?.description}
                          </td>

                          <td className="align-middle">
                            {moment(cuenta.expense_date || cuenta.expired_at).format("DD MMM YYYY")}
                          </td>
                          <td className="align-middle text-right text-nowrap">
                            <NumberFormat
                              displayType="text"
                              prefix={`${currency?.currency?.iso} `}
                              allowedDecimalSeparators={true}
                              decimalScale={2}
                              fixedDecimalScale
                              thousandSeparator={true}
                              value={getRateForRow(cuenta)}
                            />
                          </td>

                          {isPropertyView && estimateTotalCell(cuenta, getRateForRow(cuenta))}
                          <td
                            className="align-middle text-right"
                            style={{ width: "20%" }}
                          >
                            <span className="d-block font-weight-semi-bold">
                              <NumberFormat
                                className="text-nowrap"
                                displayType="text"
                                prefix={`${baseCurrency.symbol} `}
                                allowedDecimalSeparators={true}
                                decimalScale={2}
                                fixedDecimalScale
                                thousandSeparator={true}
                                value={cuenta.value}
                              />
                            </span>
                            <NumberFormat
                                className="text-nowrap fs--2 text-600"
                                displayType="text"
                                prefix={`Bs `}
                                allowedDecimalSeparators={true}
                                decimalScale={2}
                                fixedDecimalScale
                                thousandSeparator={true}
                                value={isPropertyView ? Number(cuenta.value) * getRateForRow(cuenta) : cuenta.amount_converted ?? Number(cuenta.value) * getRateForRow(cuenta)}
                            />
                          </td>
                        </tr>
                      ))}
                      <tr>
                        <td className="align-middle" colSpan={5}>Total de gastos particulares</td>
                        <td className="align-middle text-right font-weight-semi-bold">
                          <span className="d-block">
                            <NumberFormat
                              className="text-nowrap"
                              displayType="text"
                              prefix={`${baseCurrency.symbol} `}
                              allowedDecimalSeparators={true}
                              decimalScale={2}
                              fixedDecimalScale
                              thousandSeparator={true}
                              value={amountI}
                            />
                          </span>
                          {Boolean(currency?.currency?.iso) && (
                            <span className="d-block text-700 fs--2 text-nowrap">
                              <NumberFormat
                                className="text-right text-nowrap"
                                displayType="text"
                                allowedDecimalSeparators={true}
                                thousandSeparator={true}
                                decimalScale={2}
                                fixedDecimalScale
                                prefix={currency.currency.iso + " "}
                                value={getConvertedTotal(reportI, amountI, "particular")}
                              />
                            </span>
                          )}
                        </td>
                      </tr>
                    </>
                  </tbody>
                </Table>
              </div>
            )}

            {funds.length > 0 && (
              <div className="table-responsive mt-4 fs--1">
                <Table striped className="border-bottom">
                  <thead>
                    <tr>
                      <th className="border-0">Fondos de reserva</th>
                      <th className="border-0">Descripción</th>
                      <th className="border-0" style={{ whiteSpace: "nowrap" }}>Fecha del gasto</th>
                      <th className="border-0 text-right" style={{ whiteSpace: "nowrap" }}>Tasa del día</th>
                      {isPropertyView && <th className="border-0 text-right">Monto</th>}
                      <th className="border-0 text-right">{isPropertyView ? "Cuota" : "Monto"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {funds.map((cuenta, index) => (
                      <tr key={index}>
                        <td className="align-middle" style={{ width: "30%" }}>
                          {" "}
                          <h6 className="mb-0">{cuenta.name}</h6>
                        </td>
                        <td className="align-middle" style={{ width: "25%" }}>
                          {cuenta.description}
                        </td>

                        <td className="align-middle">
                          {appliedRateDateLabel}
                        </td>
                        <td className="align-middle text-right text-nowrap">
                          <NumberFormat
                            displayType="text"
                            prefix={`${currency?.currency?.iso} `}
                            allowedDecimalSeparators={true}
                            decimalScale={2}
                            fixedDecimalScale
                            thousandSeparator={true}
                            value={rateValue || 0}
                          />
                        </td>

                        {isPropertyView && estimateTotalCell(cuenta, rateValue)}
                        <td
                          className="align-middle text-right"
                          style={{ width: "20%" }}
                        >
                          <span className="d-block font-weight-semi-bold">
                            <NumberFormat
                              className="text-nowrap"
                              displayType="text"
                              prefix={`${baseCurrency.symbol} `}
                              allowedDecimalSeparators={true}
                              decimalScale={2}
                              fixedDecimalScale
                              thousandSeparator={true}
                              value={cuenta.amount}
                            />
                          </span>
                          <NumberFormat
                              className="text-nowrap fs--2 text-600"
                              displayType="text"
                              prefix={`Bs `}
                              allowedDecimalSeparators={true}
                              decimalScale={2}
                              fixedDecimalScale
                              thousandSeparator={true}
                              value={Number(cuenta.amount) * (rateValue || 0)}
                          />
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td className="align-middle" colSpan={isPropertyView ? 5 : 4}>Total fondo de reserva</td>
                      <td className="align-middle text-right font-weight-semi-bold">
                        <span className="d-block">
                          <NumberFormat
                            className="text-nowrap"
                            displayType="text"
                            prefix={`${baseCurrency.symbol} `}
                            allowedDecimalSeparators={true}
                            decimalScale={2}
                            fixedDecimalScale
                            thousandSeparator={true}
                            value={amountF}
                          />
                        </span>
                        {Boolean(currency?.currency?.iso) && (
                          <span className="d-block text-700 fs--2 text-nowrap">
                            <NumberFormat
                              className="text-right text-nowrap"
                              displayType="text"
                              allowedDecimalSeparators={true}
                              thousandSeparator={true}
                              decimalScale={2}
                              fixedDecimalScale
                              prefix={currency.currency.iso + " "}
                              value={sectionBs("funds", Number(amountF) * (rateValue || 0))}
                            />
                          </span>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            )}

            {incomesCommon.length > 0 && (
              <div className="table-responsive mt-4 fs--1">
                <Table striped className="border-bottom">
                  <thead>
                    <tr>
                      <th className="border-0">Ingresos comunes</th>
                      <th className="border-0">Descripción</th>
                      <th className="border-0" style={{ whiteSpace: "nowrap" }}>Fecha del ingreso</th>
                      <th className="border-0 text-right" style={{ whiteSpace: "nowrap" }}>Tasa del día</th>
                      {isPropertyView && <th className="border-0 text-right">Monto</th>}
                      <th className="border-0 text-right">{isPropertyView ? "Descuento" : "Monto"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {incomesCommon.map((income, index) => (
                      <tr key={index}>
                        <td className="align-middle" style={{ width: "40%" }}>
                          <h6 className="mb-0">{income.name}</h6>
                        </td>
                        <td className="align-middle" style={{ width: "30%" }}>
                          {income.description}
                        </td>
                        <td className="align-middle">
                          {moment(income.expense_date || income.created_at).format("DD MMM YYYY")}
                        </td>
                        <td className="align-middle text-right text-nowrap">
                          <NumberFormat
                            displayType="text"
                            prefix={`${currency?.currency?.iso} `}
                            allowedDecimalSeparators={true}
                            decimalScale={2}
                            fixedDecimalScale
                            thousandSeparator={true}
                            value={getRateForRow(income)}
                          />
                        </td>
                        {isPropertyView && estimateTotalCell(income, getRateForRow(income))}
                        <td
                          className="align-middle text-right"
                          style={{ width: "20%" }}
                        >
                          <span className="d-block font-weight-semi-bold">
                            <NumberFormat
                              className="text-nowrap"
                              displayType="text"
                              prefix={`${baseCurrency.symbol} `}
                              allowedDecimalSeparators={true}
                              decimalScale={2}
                              fixedDecimalScale
                              thousandSeparator={true}
                              value={income.amount}
                            />
                          </span>
                          {Boolean(currency?.currency?.iso) && (
                            <span className="d-block text-700 fs--2 text-nowrap">
                              <NumberFormat
                                className="text-right text-nowrap"
                                displayType="text"
                                allowedDecimalSeparators={true}
                                thousandSeparator={true}
                                decimalScale={2}
                                fixedDecimalScale
                                prefix={currency.currency.iso + " "}
                                value={Number(income.amount) * getRateForRow(income)}
                              />
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td className="align-middle" colSpan={isPropertyView ? 5 : 4}>Total ingresos comunes (se descuenta del total a pagar)</td>
                      <td className="align-middle text-right font-weight-semi-bold">
                        <span className="d-block">
                          <NumberFormat
                            className="text-nowrap"
                            displayType="text"
                            prefix="- $ "
                            allowedDecimalSeparators={true}
                            decimalScale={2}
                            fixedDecimalScale
                            thousandSeparator={true}
                            value={amountIncomesCommon}
                          />
                        </span>
                        {Boolean(currency?.currency?.iso) && (
                          <span className="d-block text-700 fs--2 text-nowrap">
                            <NumberFormat
                              className="text-right text-nowrap"
                              displayType="text"
                              allowedDecimalSeparators={true}
                              thousandSeparator={true}
                              decimalScale={2}
                              fixedDecimalScale
                              prefix={"- " + currency.currency.iso + " "}
                              value={getConvertedTotal(incomesCommon, amountIncomesCommon, "incomes_common")}
                            />
                          </span>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            )}

            {incomesIndividual.length > 0 && (
              <div className="table-responsive mt-4 fs--1">
                <Table striped className="border-bottom">
                  <thead>
                    <tr>
                      <th className="border-0 col-3">Ingresos individuales</th>
                      <th className="border-0">Descripción</th>
                      <th className="border-0" style={{ whiteSpace: "nowrap" }}>Fecha del ingreso</th>
                      <th className="border-0 text-right" style={{ whiteSpace: "nowrap" }}>Tasa del día</th>
                      {isPropertyView && <th className="border-0 text-right">Monto</th>}
                      <th className="border-0 text-right">{isPropertyView ? "Descuento" : "Monto"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {incomesIndividual.map((income, index) => (
                      <tr key={index}>
                        <td className="align-middle" style={{ width: "40%" }}>
                          <h6 className="mb-0">{income.name}</h6>
                        </td>
                        <td className="align-middle" style={{ width: "30%" }}>
                          {income.description}
                        </td>
                        <td className="align-middle">
                          {moment(income.expense_date || income.created_at).format("DD MMM YYYY")}
                        </td>
                        <td className="align-middle text-right text-nowrap">
                          <NumberFormat
                            displayType="text"
                            prefix={`${currency?.currency?.iso} `}
                            allowedDecimalSeparators={true}
                            decimalScale={2}
                            fixedDecimalScale
                            thousandSeparator={true}
                            value={getRateForRow(income)}
                          />
                        </td>
                        {isPropertyView && estimateTotalCell(income, getRateForRow(income))}
                        <td
                          className="align-middle text-right"
                          style={{ width: "20%" }}
                        >
                          <span className="d-block font-weight-semi-bold">
                            <NumberFormat
                              className="text-nowrap"
                              displayType="text"
                              prefix={`${baseCurrency.symbol} `}
                              allowedDecimalSeparators={true}
                              decimalScale={2}
                              fixedDecimalScale
                              thousandSeparator={true}
                              value={income.amount}
                            />
                          </span>
                          {Boolean(currency?.currency?.iso) && (
                            <span className="d-block text-700 fs--2 text-nowrap">
                              <NumberFormat
                                className="text-right text-nowrap"
                                displayType="text"
                                allowedDecimalSeparators={true}
                                thousandSeparator={true}
                                decimalScale={2}
                                fixedDecimalScale
                                prefix={currency.currency.iso + " "}
                                value={Number(income.amount) * getRateForRow(income)}
                              />
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td colSpan={isPropertyView ? 5 : 4}>Total ingresos individuales (se descuenta del total a pagar)</td>
                      <td className="align-middle text-right font-weight-semi-bold">
                        <span className="d-block">
                          <NumberFormat
                            className="text-nowrap"
                            displayType="text"
                            prefix="- $ "
                            allowedDecimalSeparators={true}
                            decimalScale={2}
                            fixedDecimalScale
                            thousandSeparator={true}
                            value={amountIncomesIndividual}
                          />
                        </span>
                        {Boolean(currency?.currency?.iso) && (
                          <span className="d-block text-700 fs--2 text-nowrap">
                            <NumberFormat
                              className="text-right text-nowrap"
                              displayType="text"
                              allowedDecimalSeparators={true}
                              thousandSeparator={true}
                              decimalScale={2}
                              fixedDecimalScale
                              prefix={"- " + currency.currency.iso + " "}
                              value={getConvertedTotal(incomesIndividual, amountIncomesIndividual, "incomes_individual")}
                            />
                          </span>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            )}

            <div className="table-responsive mt-4 fs--1">
              <Table striped className="border-bottom">
                <thead>
                  <tr>
                    <th className="border-0" colSpan={1}>
                      {isPropertyView
                        ? `Total a pagar propietario (${selectedAliquot?.property?.user?.data?.firstname || ""} ${
                            selectedAliquot?.property?.user?.data?.lastname || ""
                          })`
                        : "Total gastos condominio"}
                    </th>
                    <th className="border-0 text-right font-weight-semi-bold">
                      {" "}
                      <span className="d-block">
                        <NumberFormat
                          displayType="text"
                          prefix={`${baseCurrency.symbol} `}
                          allowedDecimalSeparators={true}
                          decimalScale={2}
                          fixedDecimalScale
                          thousandSeparator={true}
                          value={total.toFixed(2)}
                        />
                      </span>
                      {Boolean(currency?.currency?.iso) && (
                        <span className="d-block text-700 fs--2 text-nowrap font-weight-normal">
                          <NumberFormat
                            className="text-right text-nowrap"
                            displayType="text"
                            allowedDecimalSeparators={true}
                            thousandSeparator={true}
                            decimalScale={2}
                            fixedDecimalScale
                            prefix={currency.currency.iso + " "}
                            value={convertedTotal}
                          />
                        </span>
                      )}
                    </th>
                  </tr>
                </thead>
              </Table>
              {loadingConfig ? (
                <Spinner />
              ) : (
                <div className="text-start text-muted mt-3 pt-1 mb-0">
                  <Logo
                    to={null}
                    styleImage={{
                      width: "100px",
                    }}
                    className={"mb-2"}
                  />
                  <div className="mb-0">
                    {dataConfig?.data?.note !== "" && (
                      <FontAwesomeIcon
                        icon={faInfoCircle}
                        className="mr-2"
                        size="sm"
                      />
                    )}
                    <pre
                      style={{
                        fontFamily: "Open Sans",
                        fontSize: ".83333rem",
                        color: "#5e6e82",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {dataConfig?.data.note}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          </CardBody>
        )}
      </Card>
      <Modal isOpen={modalIsOpenPeriod}>
        <Card style={{ padding: "20px" }}>
          <ModalHeader>Realizar cobro de gastos</ModalHeader>
          <ModalBody>
            <Alert color="info" className="shadow-sm">
              <p className="fs--1">
                Si deseas gestionar los períodos solo necesitas dirigirte al{" "}
                <Link
                  to={{
                    pathname: `/dashboard/periods`,
                  }}
                >
                  módulo de períodos
                </Link>
                , donde encontrarás la opción para crear nuevos períodos.
              </p>
            </Alert>
            <Alert color="warning" className="shadow-sm">
              <h5 className="alert-heading text-center">
                INFORMACIÓN IMPORTANTE
              </h5>

              <p className="fs--1">
                Una vez que se ha seleccionado un periodo y se ha confirmado el
                cobro de los gastos para ese periodo, no es posible realizar
                nuevamente el cobro de gastos para el mismo periodo.
              </p>
            </Alert>
          </ModalBody>

          <Label for="period_id">Seleccionar período</Label>
          <Row form className="mb-3">
            <Col sm tag={FormGroup}>
              <PeriodSelect
                ref={(el) => (periodSelectRefs.current.execute = el)}
                inputId="period_id"
                condominiumId={selectedCondominium}
                value={periodId}
                onChange={handlePeriodSelectChange}
                placeholder="Selecciona un período"
              />
            </Col>
          </Row>
          {(() => {
            const selectedPeriodForDiscount = selectedPeriodObject;
            if (
              !selectedPeriodForDiscount?.discount_percentage ||
              !selectedPeriodForDiscount?.discount_due_date
            ) {
              return null;
            }
            const discountedTotal =
              Number(total) *
              (1 - Number(selectedPeriodForDiscount.discount_percentage) / 100);
            return (
              <Alert color="success" className="shadow-sm">
                <p className="fs--1 mb-0">
                  Este período tiene <strong>{selectedPeriodForDiscount.discount_percentage}%</strong>{" "}
                  de descuento por pronto pago para quien pague hasta el{" "}
                  <strong>
                    {moment(selectedPeriodForDiscount.discount_due_date).format(
                      "DD MMM YYYY"
                    )}
                  </strong>
                  . Con el total actual (
                  <NumberFormat
                    className="text-nowrap"
                    displayType="text"
                    prefix={`${baseCurrency.symbol} `}
                    decimalScale={2}
                    fixedDecimalScale
                    thousandSeparator
                    value={total}
                  />
                  ), el total combinado con descuento sería aproximadamente{" "}
                  <strong>
                    <NumberFormat
                      className="text-nowrap"
                      displayType="text"
                      prefix={`${baseCurrency.symbol} `}
                      decimalScale={2}
                      fixedDecimalScale
                      thousandSeparator
                      value={discountedTotal}
                    />
                  </strong>{" "}
                  (cada propietario ve el detalle exacto de su planilla al pagar).
                </p>
              </Alert>
            );
          })()}
          <FormGroup check className="mb-3 ml-1">
            <Label for="auto_pay_with_credit_balance_emit" check>
              <Input
                id="auto_pay_with_credit_balance_emit"
                type="checkbox"
                checked={autoPayEnabled}
                onChange={(e) => setAutoPayEnabled(e.target.checked)}
              />
              <strong>Pagar con saldo a favor</strong>
            </Label>
            <small className="d-block text-600 fs--1">
              Al emitir, a cada propietario que tenga saldo a favor se le paga la
              planilla con ese saldo. Si no alcanza, se abona lo que haya,
              empezando por la deuda más vieja. Se recuerda para las próximas
              emisiones.
            </small>
          </FormGroup>
          <FormGroup className="mb-3">
            <Label for="emission_note">Nota de la planilla</Label>
            <Input
              id="emission_note"
              type="textarea"
              rows={3}
              maxLength={720}
              value={emissionNote}
              onChange={(e) => setEmissionNote(e.target.value)}
            />
            <small className="d-block text-600 fs--1">
              Se guarda con las planillas de este período. Si después cambias la
              nota en Configuración, estas planillas no cambian.
            </small>
          </FormGroup>
          <ModalFooter>
            <Button onClick={closeModalPeriod} icon={faTimes}>
              Cancelar
            </Button>
            <Button
              data-testid="submit-button"
              color="primary"
              type="submit"
              disabled={!periodId || isEmitting}
              onClick={() => handleExecuteJobs(periodId)}
            >
              {" "}
              {isEmitting ? "Emitiendo..." : "Sí, realizar cobro de gastos"}
            </Button>
          </ModalFooter>
        </Card>
      </Modal>
      <Modal
        isOpen={modalIsOpenPeriodRegister}
      >
        <Card style={{ padding: "20px" }}>
          <ModalHeader>Crear cuentas por pagar</ModalHeader>
          <ModalBody>
            <Alert color="info" className="shadow-sm">
              <p className="fs--1">
                Si deseas gestionar los períodos solo necesitas dirigirte al{" "}
                <Link
                  to={{
                    pathname: `/dashboard/periods`,
                  }}
                >
                  módulo de períodos
                </Link>
                , donde encontrarás la opción para crear nuevos períodos.
              </p>
            </Alert>
            <Alert color="warning" className="shadow-sm">
              <h5 className="alert-heading text-center">
                INFORMACIÓN IMPORTANTE
              </h5>

              <p className="fs--1">
                Una vez que se ha seleccionado un periodo y se ha confirmado el
                cobro de los gastos para ese periodo, no es posible realizar
                nuevamente el cobro de gastos para el mismo periodo.
              </p>
            </Alert>
          </ModalBody>

          <Label for="period_id">Seleccionar período</Label>
          <Row form className="mb-3">
            <Col sm tag={FormGroup}>
              <PeriodSelect
                ref={(el) => (periodSelectRefs.current.register = el)}
                inputId="period_id"
                condominiumId={selectedCondominium}
                value={periodId}
                onChange={handlePeriodSelectChange}
                placeholder="Selecciona un período"
              />
            </Col>
          </Row>
          <ModalFooter>
            <Button onClick={closeModalPeriodRegister} icon={faTimes}>
              Cancelar
            </Button>
            <Button
              data-testid="submit-button"
              color="primary"
              type="submit"
              disabled={!periodId}
              onClick={() => handleExecuteDebtsToPayJobs(periodId)}
            >
              {" "}
              Sí, crear cuentas por pagar
            </Button>
          </ModalFooter>
        </Card>
      </Modal>
      <Modal
        isOpen={modalIsOpenPeriodEmail}
      >
        <Card style={{ padding: "20px" }}>
          <ModalHeader>Enviar avisos de cobro</ModalHeader>
          <ModalBody>
            <Alert color="info" className="shadow-sm">
              <h5 className="alert-heading text-center">
                INFORMACIÓN IMPORTANTE
              </h5>

              <p className="fs--1">
                Si deseas gestionar los períodos solo necesitas dirigirte al{" "}
                <Link
                  to={{
                    pathname: `/dashboard/periods`,
                  }}
                >
                  módulo de períodos
                </Link>
                , donde encontrarás la opción para crear nuevos períodos.
              </p>
            </Alert>
          </ModalBody>

          <Label for="period_id">Seleccionar período</Label>
          <Row form className="mb-3">
            <Col sm tag={FormGroup}>
              <PeriodSelect
                ref={(el) => (periodSelectRefs.current.emailOwners = el)}
                inputId="period_id"
                condominiumId={selectedCondominium}
                value={periodId}
                onChange={handlePeriodSelectChange}
                placeholder="Selecciona un período"
              />
            </Col>
          </Row>
          <ModalFooter>
            <Button onClick={closeModalPeriodEMail} icon={faTimes}>
              Cancelar
            </Button>
            <Button
              data-testid="submit-button"
              color="primary"
              type="submit"
              disabled={!periodId}
              onClick={() => handleSendEmail(periodId)}
            >
              {" "}
              Sí, enviar avisos de cobro
            </Button>
          </ModalFooter>
        </Card>
      </Modal>{" "}
      <Modal isOpen={modalIsOpenAdmin}>
        <Card style={{ padding: "20px" }}>
          <ModalHeader>Enviar avisos de cobro al administrador</ModalHeader>
          <ModalBody>
            <Alert color="info" className="shadow-sm">
              <h5 className="alert-heading text-center">
                INFORMACIÓN IMPORTANTE
              </h5>

              <p className="fs--1">
                Si deseas gestionar los períodos solo necesitas dirigirte al{" "}
                <Link
                  to={{
                    pathname: `/dashboard/periods`,
                  }}
                >
                  módulo de períodos
                </Link>
                , donde encontrarás la opción para crear nuevos períodos.
              </p>
            </Alert>
          </ModalBody>

          <Label for="period_id">Seleccionar período</Label>
          <Row form className="mb-3">
            <Col sm tag={FormGroup}>
              <PeriodSelect
                ref={(el) => (periodSelectRefs.current.emailAdmin = el)}
                inputId="period_id"
                condominiumId={selectedCondominium}
                value={periodId}
                onChange={handlePeriodSelectChange}
                placeholder="Selecciona un período"
              />
            </Col>
          </Row>
          <ModalFooter>
            <Button
              onClick={closeModalAdmin}
              icon={faTimes}
              disabled={isLoadingAdmin}
            >
              Cancelar
            </Button>
            <Button
              data-testid="submit-button"
              color="primary"
              type="submit"
              disabled={!periodId || isLoadingAdmin}
              onClick={() => handleSendEmailAdmin(periodId)}
            >
              {isLoadingAdmin ? (
                <>
                  <Spinner size="sm" /> Enviando...
                </>
              ) : (
                "Sí, enviar avisos de cobro"
              )}
            </Button>
          </ModalFooter>
        </Card>
      </Modal>
    </>
  );
};

SingleForm.propTypes = {
  showPagination: PropTypes.bool,
};

export default withRedirect(SingleForm);
